// OVER 3 QUICK BOT — hot-pair scanning digit bot with SINGLE-TRADE recovery.
// Wins when the last digit of the settlement tick is 4–9 (~60% of ticks).
// SCANNER: streams ticks from all markets, ranks them by recent Over-3 hit
// rate; Auto mode always enters on the hottest pair.
// RECOVERY: after a loss the bot claws the deficit back +12% in TWO
// half-target slices — smaller rungs, longer survivable streaks, then resets
// to base. "Until recovered" keeps going; the stop-loss is the hard brake.
// TAKE-PROFIT: hard stop + celebration popup + auto-brag to Telegram (real accts).
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Zap, Loader2, Square, Shield, Target, Radar, Flame, PartyPopper, X } from "lucide-react";
import { send, subscribeTicks } from "@/lib/deriv";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { api } from "@/lib/api";
import { celebrateWin } from "@/lib/celebrate";
import BotLiveView from "@/components/dashboard/BotLiveView";
import { getRecovTarget, RISK_WARNING, LADDER_PAUSE_MSG } from "@/lib/recovery";

const OVERSHOOT = () => 1 + getRecovTarget(); // recovery target: deficit + user target (0/5/10%, default 5%)
const SLICES = 2;        // 🔁 clawed back in 2 half-target slices — smaller rungs, longer runs
// CONTRACT MODES — Over 2 & Under 7 only (70% tier). The 60% tier was
// trialled and removed (edge gap too wide — user decision).
const MODES = {
  over2:  { id: "over2",  label: "Over 2",  ct: "DIGITOVER",  barrier: 2, payoutR: 0.36,  winPct: 70, payoutX: "1.36", digits: "3–9", cover: 7, balDiv: 1200, chance: "0.07%", isWin: (d) => d > 2 },
  under7: { id: "under7", label: "Under 7", ct: "DIGITUNDER", barrier: 7, payoutR: 0.365, winPct: 70, payoutX: "1.37", digits: "0–6", cover: 6, balDiv: 1200, chance: "0.07%", isWin: (d) => d < 7 },
};
const SYMBOLS = [
  { id: "1HZ75V", label: "Vol 75 (1s)" },
  { id: "1HZ50V", label: "Vol 50 (1s)" },
  { id: "1HZ25V", label: "Vol 25 (1s)" },
  { id: "1HZ10V", label: "Vol 10 (1s)" },
  { id: "1HZ100V", label: "Vol 100 (1s)" },
  { id: "R_100", label: "Vol 100" },
  { id: "R_75", label: "Vol 75" },
  { id: "R_50", label: "Vol 50" },
  { id: "R_25", label: "Vol 25" },
  { id: "R_10", label: "Vol 10" },
];
// 🎯 20-tick ranking window (user rule: long windows dilute what the pair is
// doing NOW) — 10 pairs scanned, only the TOP 1 is ever traded.
const WINDOW = 20;
const r2 = (v) => Math.round(v * 100) / 100;
const lastDigit = (q) => Number(String(q).replace(".", "").slice(-1));
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const symLabel = (id) => SYMBOLS.find((s) => s.id === id)?.label || id;

const Over3RecoveryBot = ({ authorized, demoMode, currency, balance, onTradeSettled, onGoToAutoBot }) => {
  const { session } = useAuth();
  const [modeChoice, setModeChoice] = useState("auto"); // ⭐ Auto — best mode for the balance
  // BEST-MODE ADVISOR — computes both modes' plans for the live balance and
  // recommends the one with better odds of hitting TP before SL.
  const plans = useMemo(() => {
    const bal = Number(balance) || 0;
    if (bal < 5) return null;
    const res = {};
    for (const m of Object.values(MODES)) {
      const baseR = Math.max(0.35, Math.floor((bal / m.balDiv) * 100) / 100);
      let l = baseR, slNeed = baseR;
      for (let k = 0; k < m.cover - 1; k++) {
        const next = Math.max(0.35, r2((l * OVERSHOOT()) / SLICES / m.payoutR));
        slNeed = r2(l + next);
        l = slNeed;
      }
      const slR = Math.min(Math.ceil(slNeed), Math.max(5, Math.floor(bal * 0.95)));
      const tpR = Math.max(2, Math.round(bal * 0.10));
      let lost = baseR, streak = 1; // base loss = loss #1
      while (streak < 12) {
        const next = Math.max(0.35, r2((lost * OVERSHOOT()) / SLICES / m.payoutR));
        if (lost + next > slR) break;
        lost = r2(lost + next);
        streak += 1;
      }
      let l2 = 0.35, need = 0.35;
      for (let k = 0; k < m.cover - 1; k++) {
        const nx = Math.max(0.35, r2((l2 * OVERSHOOT()) / SLICES / m.payoutR));
        need = r2(l2 + nx);
        l2 = need;
      }
      const bust = Math.pow(1 - m.winPct / 100, streak);
      const cycles = Math.max(1, Math.ceil(tpR / (baseR * m.payoutR)));
      const tpOdds = Math.pow(1 - bust, cycles);
      res[m.id] = { bal, base: baseR, sl: slR, tp: tpR, streak, needBal: Math.ceil(need / 0.95), bust, tpOdds };
    }
    res.best = res.over2.tpOdds >= res.under7.tpOdds ? "over2" : "under7";
    return res;
  }, [balance]);
  const mode = modeChoice === "auto" ? (plans?.best || "under7") : modeChoice;
  const M = MODES[mode];
  const advisor = plans ? plans[mode] : null;
  const modeRef = useRef(MODES.under7);
  const [pairMode, setPairMode] = useState("auto");
  const [recMode] = useState("inf"); // caps removed — always run until recovered (user request)
  const [baseStake, setBaseStake] = useState("0.35");
  // 🪜 depth cap — recovery rounds before the bot pauses itself (default 4, max 6)
  const [depthCap, setDepthCap] = useState(() => { try { const v = parseInt(localStorage.getItem("vts_over3_depth"), 10); return [4, 5, 6].includes(v) ? v : 4; } catch { return 4; } });
  const depthCapRef = useRef(4);
  useEffect(() => { depthCapRef.current = depthCap; try { localStorage.setItem("vts_over3_depth", String(depthCap)); } catch { /* noop */ } }, [depthCap]);
  const [sl, setSl] = useState("10");
  const [tp, setTp] = useState("5");
  const [running, setRunning] = useState(false);
  const [recInfo, setRecInfo] = useState({ round: 0, deficit: 0 });
  const [net, setNet] = useState(0);
  const [wins, setWins] = useState(0);
  const [losses, setLosses] = useState(0);
  const [phase, setPhase] = useState("idle");
  const [activeSym, setActiveSym] = useState(null);
  const [log, setLog] = useState([]);
  const [digitMap, setDigitMap] = useState({});
  const [tpHit, setTpHit] = useState(null); // {profit, wins, losses, minutes, shared}
  const [patience, setPatience] = useState(true); // DIGIT MATRIX smart entry — ON by default
  const [matrixMsg, setMatrixMsg] = useState(null); // "waiting" | "aligned" | "timeout"
  const [slStopped, setSlStopped] = useState(false); // show the +EV nudge after a losing stop
  const [showWarn, setShowWarn] = useState(false); // responsible-use popup before starting
  const [serverMode, setServerMode] = useState(false); // ☁️ 24/7 backend session (SL+TP mandatory)
  const [autoRestart, setAutoRestart] = useState(false); // 🔁 bank TP and start a fresh session
  const [banked, setBanked] = useState(0); // total banked across auto-restarted in-app sessions
  const [srv, setSrv] = useState(null); // server bot status payload
  const runningRef = useRef(false);
  const netRef = useRef(0);
  const winsRef = useRef(0);
  const lossesRef = useRef(0);
  const startAtRef = useRef(0);
  const digitMapRef = useRef({});
  const lastLossSymRef = useRef(null); // pair that just lost — excluded from next pick
  const patienceRef = useRef(true);
  useEffect(() => { patienceRef.current = patience; }, [patience]);
  // mode is FROZEN while a recovery ladder is open — switching payout ratios
  // mid-ladder would inflate the recovery stakes
  useEffect(() => { if (!running || recInfo.round === 0) modeRef.current = MODES[mode]; }, [mode, running, recInfo.round]);
  const autoRestartRef = useRef(false);
  const bankedRef = useRef(0);
  useEffect(() => { autoRestartRef.current = autoRestart; }, [autoRestart]);

  // SCANNER: stream ticks from every market with a self-healing watchdog —
  // socket re-auth (login / demo↔real switch) silently kills subscriptions,
  // so any pair quiet for >30s gets re-subscribed automatically.
  const unsubsRef = useRef({});
  const lastTickAtRef = useRef({});
  const subscribePair = useCallback((id) => {
    try { unsubsRef.current[id]?.(); } catch { /* ignore */ }
    try {
      unsubsRef.current[id] = subscribeTicks(id, (msg) => {
        const q = msg?.tick?.quote; if (q == null) return;
        lastTickAtRef.current[id] = Date.now();
        const arr = [...(digitMapRef.current[id] || []), lastDigit(q)].slice(-WINDOW);
        digitMapRef.current = { ...digitMapRef.current, [id]: arr };
        setDigitMap(digitMapRef.current);
      });
    } catch { /* watchdog retries */ }
  }, []);

  useEffect(() => {
    SYMBOLS.forEach((s) => subscribePair(s.id));
    const iv = setInterval(() => {
      const now = Date.now();
      SYMBOLS.forEach((s) => {
        if (now - (lastTickAtRef.current[s.id] || 0) > 30000) subscribePair(s.id);
      });
    }, 20000);
    return () => {
      clearInterval(iv);
      Object.values(unsubsRef.current).forEach((u) => { try { u?.(); } catch { /* ignore */ } });
    };
  }, [subscribePair]);

  const ranking = useMemo(() => (
    SYMBOLS.map((s) => {
      const arr = digitMap[s.id] || [];
      const hits = arr.filter(M.isWin).length;
      return { ...s, n: arr.length, rate: arr.length ? (hits / arr.length) * 100 : 0 };
    }).sort((a, b) => b.rate - a.rate)
  ), [digitMap, M]);

  const pickSymbol = useCallback((excludeId = null) => {
    if (pairMode !== "auto") return pairMode;
    // prefer pairs with enough scanner data, but ALWAYS honour the exclusion —
    // even with zero data the loser must rotate out to the next pair
    let pool = ranking.filter((s) => s.n >= 20);
    if (pool.length === 0) pool = ranking.length ? ranking : SYMBOLS.map((s) => ({ ...s }));
    if (excludeId && pool.length > 1) pool = pool.filter((s) => s.id !== excludeId);
    return (pool[0] || SYMBOLS[0]).id;
  }, [pairMode, ranking]);
  // the run-loop must always use the LATEST picker (fresh ranking), not the
  // one captured when START was pressed
  const pickSymbolRef = useRef(pickSymbol);
  useEffect(() => { pickSymbolRef.current = pickSymbol; }, [pickSymbol]);

  const stopBot = useCallback((reason) => {
    runningRef.current = false;
    setRunning(false);
    setPhase("idle");
    if (netRef.current < 0) setSlStopped(true);
    if (reason) toast.info(`${modeRef.current.label} bot stopped`, { description: reason });
  }, []);

  // place ONE contract and wait for its 1-tick settlement
  const placeAndSettle = useCallback(async (sym, stake) => {
    const md = modeRef.current;
    const params = { amount: stake, basis: "stake", contract_type: md.ct, currency: currency || "USD", duration: 1, duration_unit: "t", underlying_symbol: sym, barrier: md.barrier };
    let buy = await send({ buy: 1, price: stake, parameters: params });
    if (buy?.error && /rate limit/i.test(buy.error.message || "")) {
      await sleep(2500);
      buy = await send({ buy: 1, price: stake, parameters: params });
    }
    if (buy?.error || !buy?.buy?.contract_id) return { fail: buy?.error?.message || "Buy failed" };
    const cid = String(buy.buy.contract_id);
    try {
      await api.post("/track/trade", { account_id: session?.account_id || "unknown", contract_id: cid, symbol: sym, contract_type: md.ct, stake: parseFloat(buy.buy.buy_price), payout: parseFloat(buy.buy.payout), currency: currency || "USD", strategy: `${md.id}_recovery` });
    } catch { /* non-blocking */ }
    const deadline = Date.now() + 30000;
    while (Date.now() < deadline) {
      await sleep(900);
      const st = await send({ proposal_open_contract: 1, contract_id: cid });
      const c = st?.proposal_open_contract;
      if (c?.is_sold) {
        const profit = Number(c.profit ?? 0);
        // ONLY the exit tick is the settlement tick — current_spot is a LATER
        // tick. If only the raw float exists, re-pad it to the market's display
        // precision (floats drop trailing zeros -> wrong digit).
        const digitSpot = (cc) => {
          let s = cc?.exit_spot ?? cc?.exit_tick_display_value;
          if (s == null || s === "") {
            const raw = cc?.exit_tick;
            if (raw == null) return null;
            const ref = String(cc?.entry_spot ?? cc?.entry_tick_display_value ?? cc?.current_spot_display_value ?? "");
            s = ref.includes(".") ? Number(raw).toFixed(ref.split(".")[1].length) : raw;
          }
          return s;
        };
        let spot = digitSpot(c);
        for (let k = 0; k < 3 && spot == null; k++) {
          await sleep(800);
          const st2 = await send({ proposal_open_contract: 1, contract_id: cid });
          spot = digitSpot(st2?.proposal_open_contract);
        }
        let digit = spot != null ? lastDigit(spot) : null;
        // sanity: the digit must agree with the settlement result
        if (digit != null && modeRef.current.isWin(digit) !== (profit > 0)) digit = null;
        try { await api.patch(`/track/trade/${cid}`, { status: profit > 0 ? "won" : "lost", profit }); } catch { /* ignore */ }
        try { onTradeSettled?.(profit); } catch { /* ignore */ }
        return { profit: r2(profit), digit, stake };
      }
    }
    return { fail: "Settlement timed out — check History" };
  }, [currency, session, onTradeSettled]);

  const recordResult = useCallback((sym, res, round) => {
    netRef.current = r2(netRef.current + res.profit);
    setNet(netRef.current);
    const won = res.profit > 0;
    winsRef.current += won ? 1 : 0;
    lossesRef.current += won ? 0 : 1;
    setWins(winsRef.current);
    setLosses(lossesRef.current);
    setLog((h) => [{ sym, stake: res.stake, digit: res.digit, won, profit: res.profit, step: round, at: Date.now() }, ...h.slice(0, 19)]);
  }, []);

  // DIGIT MATRIX ENTRY (default ON) — enter only when losing digits are RARE
  // in the last 20 ticks, the LAST digit was a loser, and winners dominate.
  const matrixOk = (digits) => {
    if (!digits || digits.length < 20) return false;
    const w = digits.slice(-20);
    const isWin = modeRef.current.isWin;
    const loseN = w.filter((d) => !isWin(d)).length;
    const expLose = (10 - modeRef.current.cover) / 10;
    return loseN / w.length <= expLose * 0.7 && !isWin(w[w.length - 1])
      && (1 - loseN / w.length) >= (1 - expLose) + 0.05;
  };

  const waitForWinTick = useCallback(async (sym) => {
    if (!patienceRef.current) return true;
    const digs = () => digitMapRef.current[sym] || [];
    if (matrixOk(digs())) { setMatrixMsg("aligned"); return true; }
    setPhase("gating");
    setMatrixMsg("waiting");
    const startedAt = Date.now();
    while (runningRef.current) {
      await sleep(400);
      if (matrixOk(digs())) { setMatrixMsg("aligned"); return true; }
      if (Date.now() - startedAt > 45000) { setMatrixMsg("rotating"); return "rotate"; } // bad digits — rotate pairs, NEVER force an entry
    }
    return false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runLoop = useCallback(async () => {
    const base = Math.max(0.35, parseFloat(baseStake) || 0.35);
    const slV = Math.max(0, parseFloat(sl) || 0);
    const tpV = Math.max(0, parseFloat(tp) || 0);
    const maxRounds = depthCapRef.current; // 🪜 depth cap (default 4, max 6) — full ladder lost ⇒ PAUSE, never restart alone
    let deficit = 0;
    let round = 0;
    const checkLimits = () => {
      if (slV > 0 && netRef.current <= -slV) { stopBot(`Stop-loss hit (−$${slV.toFixed(2)}) — hard stop, balance protected.`); return true; }
      if (tpV > 0 && netRef.current >= tpV) {
        const minutes = (Date.now() - startAtRef.current) / 60000;
        const restarted = autoRestartRef.current;
        setTpHit({ profit: netRef.current, wins: winsRef.current, losses: lossesRef.current, minutes, shared: !demoMode, restarted });
        if (!demoMode) api.post("/over3/brag", { account_id: session?.account_id || "", profit: netRef.current, wins: winsRef.current, losses: lossesRef.current, minutes: r2(minutes), bot_label: `${modeRef.current.label} Quick Bot` }).catch(() => {});
        if (restarted) {
          bankedRef.current = r2(bankedRef.current + netRef.current);
          setBanked(bankedRef.current);
          toast.success(`🏦 TP banked +$${netRef.current.toFixed(2)}`, { description: `Total banked $${bankedRef.current.toFixed(2)} — starting a fresh session` });
          netRef.current = 0; winsRef.current = 0; lossesRef.current = 0;
          setNet(0); setWins(0); setLosses(0);
          deficit = 0; round = 0; lastLossSymRef.current = null;
          startAtRef.current = Date.now();
          setRecInfo({ round: 0, deficit: 0 });
          return false; // keep the loop running
        }
        runningRef.current = false; setRunning(false); setPhase("idle");
        return true;
      }
      return false;
    };
    while (runningRef.current) {
      setPhase("scanning");
      // re-scan EVERY round; a pair that just lost is skipped → rotate to the
      // next-hottest pair instead of hammering the same one
      const sym = pickSymbolRef.current(lastLossSymRef.current);
      setActiveSym(sym);
      setRecInfo({ round, deficit });
      const gate = await waitForWinTick(sym);
      if (gate === false) return; // stopped while gating
      if (gate === "rotate") {
        // digits stayed hostile for 45s — rotate to the next-hottest pair
        // instead of forcing a bad entry (user rule: never trade into losers)
        lastLossSymRef.current = sym;
        continue;
      }
      setPhase("placing");
      if (round === 0) {
        // BASE trade — never bigger than the remaining loss budget
        if (slV > 0 && netRef.current - base < -slV) { stopBot(`Stop-loss protection: a $${base.toFixed(2)} base trade could push you past your −$${slV.toFixed(2)} limit.`); return; }
        const res = await placeAndSettle(sym, r2(base));
        if (res.fail) { if (/balance|insufficient/i.test(res.fail)) stopBot(`Balance too low — deposit or lower the base stake.`); else stopBot(res.fail); return; }
        setPhase("settling");
        recordResult(sym, res, 0);
        if (res.profit <= 0) { deficit = r2(-res.profit); round = 1; lastLossSymRef.current = sym; }
        else lastLossSymRef.current = null;
      } else {
        // RECOVERY round: ONE combined trade sized to win back the whole
        // deficit +12% (user: two split trades can go 1W/1L and still lose)
        const per = Math.max(0.35, r2((deficit * OVERSHOOT()) / SLICES / modeRef.current.payoutR));
        // HARD STOP-LOSS GATE: never stake more than the remaining loss budget.
        if (slV > 0 && netRef.current - per < -slV) {
          stopBot(`Stop-loss protection: the next recovery trade ($${per.toFixed(2)}) could exceed your −$${slV.toFixed(2)} limit — stopped BEFORE betting it. Net: ${netRef.current >= 0 ? "+" : ""}$${netRef.current.toFixed(2)}.`);
          return;
        }
        const res = await placeAndSettle(sym, per);
        setPhase("settling");
        if (res.fail) {
          if (/balance|insufficient/i.test(res.fail)) { stopBot(`Balance too low for the $${per.toFixed(2)} recovery trade.`); return; }
          stopBot(res.fail); return;
        }
        recordResult(sym, res, round);
        const roundNet = res.profit;
        deficit = r2(deficit - roundNet);
        lastLossSymRef.current = roundNet > 0 ? null : sym; // losing round → rotate away next pick
        if (deficit <= 0.01) { deficit = 0; round = 0; }                       // recovered (+ target profit)
        else if (round >= maxRounds) { stopBot(LADDER_PAUSE_MSG); return; }    // 🛑 full ladder lost — hard pause, no auto-restart
        else round += 1;
      }
      setRecInfo({ round, deficit });
      if (checkLimits()) return;
      await sleep(1200);
    }
  }, [baseStake, sl, tp, recMode, demoMode, session, placeAndSettle, recordResult, stopBot, waitForWinTick]);

  const requestStart = () => {
    if (!authorized) { toast.error("Connect your Deriv account first"); return; }
    setShowWarn(true);
  };

  const overtradeKey = () => `o3starts:${session?.account_id || "demo"}:${new Date().toDateString()}`;
  const [overtradePrompt, setOvertradePrompt] = useState(null); // {sessions} — anti-overtrading nudge
  const [otWarnOn, setOtWarnOn] = useState(true); // platform-wide admin kill-switch
  useEffect(() => {
    api.get("/platform-settings").then(({ data }) => setOtWarnOn(data?.overtrade_warning !== false)).catch(() => {});
  }, []);
  const bumpStarts = () => {
    try {
      const n = parseInt(localStorage.getItem(overtradeKey()) || "0", 10) + 1;
      localStorage.setItem(overtradeKey(), String(n));
      return n;
    } catch { return 1; }
  };
  const overtradeCheck = () => {
    if (!otWarnOn) return null; // admin disabled the warning platform-wide
    try {
      const n = parseInt(localStorage.getItem(overtradeKey()) || "0", 10);
      const bankedToday = (srv?.tp_count || 0) >= 1;
      if (n >= 2 || (bankedToday && n >= 1)) return { sessions: n, bankedToday };
    } catch { /* private mode */ }
    return null;
  };

  const start = (force = false) => {
    if (!force) {
      const risk = overtradeCheck();
      if (risk) { setOvertradePrompt(risk); return; }
    }
    bumpStarts();
    setOvertradePrompt(null);
    setShowWarn(false);
    if (serverMode) { startServer(); return; }
    runningRef.current = true; netRef.current = 0; winsRef.current = 0; lossesRef.current = 0;
    startAtRef.current = Date.now(); lastLossSymRef.current = null; bankedRef.current = 0;
    setRunning(true); setNet(0); setWins(0); setLosses(0); setBanked(0); setLog([]); setTpHit(null); setSlStopped(false); setRecInfo({ round: 0, deficit: 0 });
    toast.success(`${M.label} bot started`, { description: `${pairMode === "auto" ? "Auto hot-pair" : symLabel(pairMode)} · 2-slice recovery (+${Math.round((OVERSHOOT() - 1) * 100)}% target) · SL $${sl} / TP $${tp}` });
    runLoop();
  };

  useEffect(() => () => { runningRef.current = false; }, []);

  // ---- 24/7 SERVER MODE ----
  const prevTpRef = useRef(null);      // last seen server tp_count (null = not seeded yet)
  const prevBankedRef = useRef(0);
  const pollSrv = useCallback(async () => {
    if (!session?.account_id) return;
    try {
      const { data } = await api.get(`/over3-bot/status/${encodeURIComponent(session.account_id)}`);
      const ov = arOverrideRef.current;
      if (ov && Date.now() < ov.until) data.auto_restart = ov.value; // shield fresh toggle from stale polls
      setSrv(data);
      // 🎉 pop ONLY when the server bot BANKS a session TP (not every win)
      if (data?.running) {
        const tc = Number(data.tp_count || 0);
        if (prevTpRef.current !== null && tc > prevTpRef.current) {
          celebrateWin({
            engine: "⚡ 24/7 Digit Bot", amount: Number(data.tp || 0) || Math.max(0.01, Number(data.banked || 0) - prevBankedRef.current),
            currency: "USD", extra: `session TP banked 🏦 (bank #${tc})`, key: `o3tp:${session?.account_id}:${tc}`,
          });
        }
        prevTpRef.current = tc;
        prevBankedRef.current = Number(data.banked || 0);
      }
    } catch { /* transient */ }
  }, [session?.account_id]);
  useEffect(() => { pollSrv(); const id = setInterval(pollSrv, 6000); return () => clearInterval(id); }, [pollSrv]);

  // 🎯 24/7 TP watcher — pop the celebration when a server session banks its TP
  const prevSrvRef = useRef(null);
  useEffect(() => {
    const prev = prevSrvRef.current;
    if (prev && srv) {
      const prevTp = prev.tp_count || 0, curTp = srv.tp_count || 0;
      if (curTp > prevTp) {
        const gained = r2((srv.banked || 0) - (prev.banked || 0)) || Number(srv.tp) || 0;
        setTpHit({ profit: gained, wins: prev.wins || 0, losses: prev.losses || 0, minutes: 0, shared: !srv.is_demo, restarted: true, server: true });
      } else if (prev.running && !srv.running && srv.stopped_reason === "tp") {
        setTpHit({ profit: srv.net || 0, wins: srv.wins || 0, losses: srv.losses || 0, minutes: 0, shared: !srv.is_demo, server: true, restartWasOff: !srv.auto_restart });
      } else if (prev.running && !srv.running && srv.stopped_reason === "auth") {
        toast.error("24/7 bot lost Deriv authorization", { description: "Your login token expired — log in with Deriv again, then press START to resume", duration: 12000 });
      } else if (prev.running && !srv.running && srv.stopped_reason === "ladder_paused") {
        toast.error("Bot paused after a full losing streak. Review before restarting.", {
          description: "The 24/7 bot stopped itself — it will NOT restart on its own.", duration: 12000 });
      } else if (prev.running && !srv.running && (srv.stopped_reason === "sl" || srv.stopped_reason === "daily_cap")) {
        setSlStopped(true); // show the EV nudge + pause options
      }
    }
    prevSrvRef.current = srv;
  }, [srv]);

  const [capPrompt, setCapPrompt] = useState(null); // {lost, cap} — 20% daily guard prompt
  const pausedUntil = srv?.paused_until && new Date(srv.paused_until) > new Date() ? srv.paused_until : null;

  // EV-PROTECTION PAUSE — bank it and walk away for 5h or the rest of the day
  const pauseBot = async (m) => {
    try {
      const { data } = await api.post(`/over3-bot/pause/${encodeURIComponent(session?.account_id || "")}`, { mode: m, tz_offset_minutes: new Date().getTimezoneOffset() });
      setSrv((s) => (s ? { ...s, paused_until: data.paused_until, running: false } : { paused_until: data.paused_until, running: false }));
      setTpHit(null); setSlStopped(false); setCapPrompt(null);
      if (runningRef.current) stopBot("");
      toast.success(m === "day" ? "🛑 Digit bot stopped until midnight (your time)" : "⏸ Digit bot paused for 5 hours", { description: "Secured — the edge can't touch what you walked away with" });
    } catch {
      toast.error("Could not pause the bot");
    }
  };

  // 🩺 server-side Deriv connection self-test (env → OTP → ws → RPC)
  const [diag, setDiag] = useState(null);
  const [diagBusy, setDiagBusy] = useState(false);
  const runDiag = async () => {
    setDiagBusy(true);
    setDiag(null);
    try {
      const { data } = await api.post("/diag/deriv", {
        account_id: session?.account_id || "", access_token: session?.token || "",
      });
      setDiag(data.steps || []);
      if (data.ok) toast.success("🩺 All good — the server CAN reach Deriv on this account");
      else toast.error("🩺 Found the blocker — see the red line below");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Diagnostic call failed");
    }
    setDiagBusy(false);
  };

  const startServer = async (overrideCap = false) => {
    const slV = Number(sl), tpV = Number(tp);
    if (!(slV > 0) || !(tpV > 0)) { toast.error("24/7 mode needs BOTH a stop-loss and a take-profit — they're the only thing watching your balance while you're away"); return; }
    try {
      const { data } = await api.post("/over3-bot/start", {
        account_id: session?.account_id || "", access_token: session?.token || "",
        stake: Math.max(0.35, Number(baseStake) || 0.35), sl: slV, tp: tpV,
        patience, symbol: pairMode, rec_mode: recMode, contract: modeChoice === "auto" ? "auto" : mode, auto_restart: autoRestart, peak_lock: true, override_daily_cap: overrideCap, disclaimer_accepted: true,
        depth_cap: depthCap, recovery_target: getRecovTarget() });
      setSrv(data);
      setCapPrompt(null);
      toast.success("☁️ 24/7 server bot started", { description: `Runs even when the app is closed — auto-stops at +$${tpV} TP or −$${slV} SL` });
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      if (typeof detail === "string" && detail.startsWith("DAILY_CAP:")) {
        const [, lost, cap] = detail.split(":");
        setCapPrompt({ lost, cap });
        return;
      }
      toast.error(detail || "Could not start the 24/7 bot");
    }
  };

  const arOverrideRef = useRef(null); // {value, until} — shield the toggle from stale polls
  const toggleSrvAutoRestart = async () => {
    try {
      const { data } = await api.post(`/over3-bot/config/${encodeURIComponent(session?.account_id || "")}`, { auto_restart: !srv?.auto_restart });
      arOverrideRef.current = { value: data.auto_restart, until: Date.now() + 15000 };
      setSrv((s) => (s ? { ...s, auto_restart: data.auto_restart } : s));
      toast.success(data.auto_restart ? "🔁 Auto-restart ON" : "Auto-restart OFF", { description: data.auto_restart ? "When TP hits, the profit is banked and a fresh session starts automatically" : "The bot will stop when TP hits" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update auto-restart");
    }
  };

  const stopServer = async () => {
    try {
      await api.post(`/over3-bot/stop/${encodeURIComponent(session?.account_id || "")}`);
      toast.success("24/7 bot stopped");
      pollSrv();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Stop failed");
    }
  };

  const base = Math.max(0.35, parseFloat(baseStake) || 0.35);
  // exRecStake removed — recovery sizing is no longer disclosed in UI copy
  const hot = ranking[0];
  const streamSym = running ? (activeSym || hot?.id) : (pairMode === "auto" ? hot?.id : pairMode);
  const stream = (digitMap[streamSym] || []).slice(-16);

  const applyAdvisor = () => {
    if (!advisor) return;
    setBaseStake(advisor.base.toFixed(2));
    setSl(String(advisor.sl));
    setTp(String(advisor.tp));
    toast.success("Auto-stake applied", { description: `Base $${advisor.base.toFixed(2)} · SL $${advisor.sl} · TP $${advisor.tp} — sized for your $${advisor.bal.toFixed(2)} balance` });
  };
  const advisorApplied = advisor && parseFloat(baseStake) === advisor.base && parseFloat(sl) === advisor.sl && parseFloat(tp) === advisor.tp;
  const phaseText = { scanning: "🛰 scanning pairs…", gating: "🕰 waiting for a winning tick…", placing: "⚡ placing trade…", settling: "⏳ waiting for tick…", idle: "" }[phase];
  const curRecStake = recInfo.deficit > 0 ? Math.max(0.35, r2((recInfo.deficit * OVERSHOOT()) / SLICES / M.payoutR)) : 0;

  return (
    <div data-testid="over3-bot-panel" className="relative rounded-xl border border-vts-line bg-vts-card p-4 space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <Zap className="h-4 w-4 text-amber-400" />
        <span className="text-sm font-bold text-white">{M.label} Quick Bot</span>
        <span className="text-[10px] text-vts-silver/55 font-mono">last digit {M.digits} wins (~{M.winPct}%) · 2-slice recovery +{Math.round((OVERSHOOT() - 1) * 100)}%</span>
        {demoMode && <span className="ml-auto text-[9px] px-1.5 py-0.5 rounded bg-vts-blue/15 text-vts-blue border border-vts-blue/30 font-bold">DEMO</span>}
      </div>

      {/* CONTRACT MODE TOGGLE — Over 3 vs Under 7 */}
      {!running && !srv?.running && (
        <div data-testid="over3-mode-toggle" className="flex flex-col sm:flex-row gap-1.5">
          <button data-testid="over3-mode-auto" onClick={() => setModeChoice("auto")}
            className={`relative flex-1 rounded-lg border px-3 py-2 text-left transition ${modeChoice === "auto" ? "border-emerald-500/60 bg-emerald-500/10" : "border-vts-line bg-vts-surface/40 hover:border-vts-silver/30"}`}>
            <div className={`text-[12px] font-bold ${modeChoice === "auto" ? "text-emerald-300" : "text-white"}`}>⭐ Auto</div>
            <div className="text-[9px] text-vts-silver/55 font-mono leading-tight">📊 scans 10 pairs on the last 20 digits before EVERY trade — fires the TOP 1 only: Over 2 when 0–2 are rare, Under 7 when 7–9 are rare</div>
          </button>
          {Object.values(MODES).map((m) => (
            <button key={m.id} data-testid={`over3-mode-${m.id}`} onClick={() => setModeChoice(m.id)}
              className={`relative flex-1 rounded-lg border px-3 py-2 text-left transition ${modeChoice === m.id ? "border-amber-500/60 bg-amber-500/10" : "border-vts-line bg-vts-surface/40 hover:border-vts-silver/30"}`}>
              {plans?.best === m.id && <span data-testid={`over3-best-badge-${m.id}`} className="absolute -top-2 right-2 px-1.5 py-0.5 rounded text-[8px] font-bold bg-emerald-500 text-black">⭐ BEST FOR YOUR BALANCE</span>}
              <div className={`text-[12px] font-bold ${modeChoice === m.id ? "text-amber-300" : "text-white"}`}>{m.winPct === 60 ? "💰" : m.id === "over2" ? "⚡" : "🛡"} {m.label}</div>
              <div className="text-[9px] text-vts-silver/55 font-mono leading-tight">{m.winPct}% win · ~{m.payoutX}× payout · wins digits {m.digits}{m.winPct === 60 ? " · biggest payout, most frequent losses" : ""}</div>
            </button>
          ))}
        </div>
      )}

      {/* HOT-PAIR SCANNER */}
      <div data-testid="over3-scanner" className="rounded-lg border border-vts-line/60 bg-vts-surface/50 p-2 space-y-1.5">
        <div className="flex items-center gap-1.5">
          <Radar className="h-3 w-3 text-vts-blue" />
          <span className="text-[9px] font-bold uppercase tracking-wider text-vts-silver/60">Live {M.label} scanner · last {WINDOW} ticks</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-1">
          {ranking.map((s, i) => (
            <div key={s.id} className={`flex items-center gap-1.5 rounded-md px-2 py-1 text-[10px] font-mono ${i === 0 ? "bg-amber-500/12 border border-amber-500/30" : "bg-vts-surface"} ${running && activeSym === s.id ? "ring-1 ring-vts-blue/60" : ""}`}>
              {i === 0 && <Flame className="h-3 w-3 text-amber-400 shrink-0" />}
              <span className="text-white truncate">{s.label}</span>
              <span className={`ml-auto font-bold ${s.rate >= M.winPct ? "text-vts-profit" : "text-vts-silver/55"}`}>{s.n < 20 ? "…" : `${s.rate.toFixed(0)}%`}</span>
            </div>
          ))}
        </div>
      </div>

      {/* live digit stream of the active/hot pair — NEWEST FIRST so the next
          digit is always visible on narrow phone screens (old ones clip) */}
      <div data-testid="over3-digit-stream" className="flex items-center gap-1">
        <span className="text-[9px] text-vts-silver/45 font-mono w-16 sm:w-20 shrink-0 truncate">{symLabel(streamSym) || "—"}</span>
        {stream.length === 0 ? <span className="text-[10px] text-vts-silver/40 font-mono">waiting for ticks…</span> : (
          <div className="flex flex-row-reverse items-center gap-1 overflow-hidden flex-1 min-w-0 justify-end" dir="ltr">
            {[...stream].reverse().map((d, i) => (
              <span key={i} className={`grid h-6 w-6 shrink-0 place-items-center rounded-md text-[11px] font-mono font-bold ${M.isWin(d) ? "bg-vts-profit/15 text-vts-profit" : "bg-vts-loss/15 text-vts-loss"} ${i === 0 ? "ring-1 ring-white/40" : ""}`}>{d}</span>
            ))}
          </div>
        )}
      </div>

      {/* 📅 TODAY — the bot's full results for the UTC day; NEVER resets on
          reloads, reconnects or TP banking (answers "how much did it make?") */}
      {srv?.today && ((srv.today.wins || 0) + (srv.today.losses || 0)) > 0 && (
        <div data-testid="over3-today-stats" className="flex items-center gap-2 flex-wrap text-[10px] font-mono">
          <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-vts-surface text-vts-silver/70 border border-vts-line">📅 TODAY</span>
          <span data-testid="over3-today-net" className={`text-[13px] font-bold ${(srv.today.net || 0) >= 0 ? "text-vts-profit" : "text-vts-loss"}`}>
            {(srv.today.net || 0) >= 0 ? "+" : ""}${Number(srv.today.net || 0).toFixed(2)}
          </span>
          <span className="text-vts-silver/60">{srv.today.wins || 0}W / {srv.today.losses || 0}L</span>
          <span className="text-vts-silver/40">full bot results today — survives reloads &amp; TP banking</span>
        </div>
      )}

      {/* ☁️ 24/7 SERVER SESSION — identical design to the in-app running row */}
      {srv?.running && !running && (
        <div className="flex items-center gap-3 flex-wrap text-[11px] font-mono" data-testid="over3-server-card">
          <Loader2 className="h-3.5 w-3.5 animate-spin text-amber-400" />
          <span data-testid="over3-server-phase" className="text-vts-silver/70">
            {{ scanning: "🛰 scanning pairs…", gating: "🕰 waiting for a winning tick…", placing: "⚡ placing trade…", settling: "⏳ waiting for tick…", cooling: "🧊 loss cool-down…" }[srv.phase] || "☁️ connecting…"}
          </span>
          <span data-testid="over3-server-net" className={`text-[14px] font-bold ${(srv.net || 0) >= 0 ? "text-vts-profit" : "text-vts-loss"}`}>{(srv.net || 0) >= 0 ? "+" : ""}${(srv.net || 0).toFixed(2)}</span>
          <span className="text-vts-silver/60">{srv.wins || 0}W / {srv.losses || 0}L</span>
          {((srv.tp_count || 0) > 0 || (srv.banked || 0) > 0) && (() => {
            const rn = srv.run_net ?? Math.round(((srv.banked || 0) + (srv.net || 0)) * 100) / 100;
            return (
              <span data-testid="over3-run-total" className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${rn >= 0 ? "bg-emerald-500/15 text-emerald-300" : "bg-vts-loss/15 text-vts-loss"}`}>
                📈 since START {rn >= 0 ? "+" : ""}${Number(rn).toFixed(2)} · {srv.run_wins ?? srv.wins ?? 0}W/{srv.run_losses ?? srv.losses ?? 0}L
              </span>
            );
          })()}
          <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${(srv.round || 0) === 0 ? "bg-vts-profit/15 text-vts-profit" : "bg-amber-500/15 text-amber-300"}`}>
            {(srv.round || 0) === 0 ? "BASE STAKE" : `RECOVERY R${srv.round} · down $${(srv.deficit || 0).toFixed(2)}`}
          </span>
          <span className="text-vts-silver/45">SL −${srv.sl} · TP +${srv.tp}</span>
          {srv.patience && <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-vts-blue/15 text-vts-blue">🎯 MATRIX</span>}
          {srv.patience && (srv.phase === "gating" || srv.matrix) && (
            <span data-testid="over3-matrix-status" className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${
              srv.matrix === "aligned" ? "bg-vts-profit/15 text-vts-profit" : srv.matrix === "rotating" ? "bg-amber-500/15 text-amber-300" : "bg-vts-surface text-vts-silver/70 animate-pulse"}`}>
              {srv.matrix === "aligned" ? "🎯 matrix aligned — entering!" : srv.matrix === "rotating" ? "🔄 losers everywhere — rotating pairs" : "🔎 scanning digits — waiting for rare losers…"}
            </span>
          )}
          <button data-testid="over3-server-autorestart-toggle" onClick={toggleSrvAutoRestart}
            className={`px-1.5 py-0.5 rounded text-[9px] font-bold transition hover:brightness-125 ${srv.auto_restart ? "bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-500/40" : "bg-vts-surface text-vts-silver/50 ring-1 ring-vts-line"}`}>
            🔁 AUTO-RESTART {srv.auto_restart ? "ON" : "OFF"}
          </button>
          {(srv.banked || 0) > 0 && <span data-testid="over3-server-banked" className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-vts-profit/15 text-vts-profit">🏦 banked +${Number(srv.banked).toFixed(2)} · {srv.tp_count || 0} TP{(srv.tp_count || 0) === 1 ? "" : "s"}</span>}
          {(srv.reconnects || 0) > 1 && <span data-testid="over3-server-reconnects" className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-sky-500/10 text-sky-300/80">♻️ self-healed ×{srv.reconnects - 1}</span>}
          {(srv.otp_fails || 0) > 0 && <span data-testid="over3-server-reauth" className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-500/15 text-amber-300 animate-pulse">⚠️ reconnecting… ({srv.otp_fails}/12)</span>}
          {/* 🩺 diagnostics — surface the runner's last error so "running but
              0 trades" is never a mystery again (prod debugging aid) */}
          {srv.last_error && (
            <span data-testid="over3-server-error" className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-vts-loss/15 text-vts-loss max-w-full truncate" title={srv.last_error}>
              🩺 {String(srv.last_error).slice(0, 90)}
            </span>
          )}
          <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-500/15 text-amber-300">{srv.contract_choice === "auto" ? "⭐ AUTO · " : ""}{MODES[srv.contract]?.label?.toUpperCase() || "OVER 2"}</span>
          <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-sky-500/15 text-sky-300">☁️ 24/7 — runs with the app closed</span>
          {(srv.floor || 0) > 0 && <span data-testid="over3-peak-lock" className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-emerald-500/15 text-emerald-300">🔒 PEAK LOCK · floor +${Number(srv.floor).toFixed(2)}</span>}
          {(() => { const m = srv.last_trade_at ? Math.floor((Date.now() - new Date(srv.last_trade_at).getTime()) / 60000) : null; return m !== null && m >= 10 ? (
            <span data-testid="over3-stall-alarm" className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-vts-loss/20 text-vts-loss animate-pulse">⚠️ NO TRADES FOR {m}m — bot may be stalled, consider a restart</span>
          ) : null; })()}
          <button data-testid="over3-server-stop" onClick={stopServer}
            className="ml-auto flex items-center gap-1 rounded-lg bg-vts-loss text-white text-[11px] font-bold px-4 py-1.5 hover:brightness-110 active:scale-95 transition">
            <Square className="h-3 w-3" /> STOP
          </button>
          <div className="basis-full">
            <BotLiveView testPrefix="over3"
              digits={srv.last_digits || []}
              trades={srv.recent_trades || []}
              symbol={srv.active_sym || srv.symbol}
              isWinDigit={(d) => (MODES[srv.contract] || M).isWin(d)} />
            {((srv.cooldowns_taken || 0) > 0 || (srv.storm_pauses || 0) > 0) && (
              <div className="mt-1">
                <span data-testid="over3-guard-stats" className="px-1.5 py-0.5 rounded text-[9px] font-bold font-mono bg-vts-surface text-vts-silver/60">🛡 guards: 🧊 ×{srv.cooldowns_taken || 0} cool-downs · 🚨 ×{srv.storm_pauses || 0} storm pauses</span>
              </div>
            )}
            {srv.pause_until && srv.pause_until * 1000 > Date.now() ? (
              <div className="mt-1">
                <span data-testid="over3-cooldown" className="px-1.5 py-0.5 rounded text-[9px] font-bold font-mono bg-sky-500/15 text-sky-300">🧊 COOL-DOWN — holding fire ~{Math.max(1, Math.ceil(srv.pause_until - Date.now() / 1000))}s (loss guard / storm brake)</span>
              </div>
            ) : null}
            {(srv.pair_ranks || []).length > 0 && (
              <div data-testid="over3-pair-ranks" className="mt-1 flex items-center gap-1 flex-wrap">
                <span className="text-[8px] uppercase tracking-wider text-vts-silver/50 font-mono">Markets:</span>
                {srv.pair_ranks.map((r) => (
                  <span key={r.symbol} className={`px-1.5 py-0.5 rounded text-[9px] font-mono border ${r.symbol === (srv.active_sym || srv.symbol) ? "border-amber-400 bg-amber-400/15 text-amber-300 font-bold" : "border-vts-line bg-vts-surface/40 text-vts-silver/55"}`}>
                    {r.hot ? "🔥 " : ""}{r.label || r.symbol} {Number(r.rate).toFixed(1)}%{r.symbol === (srv.active_sym || srv.symbol) ? " · playing" : ""}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      {pausedUntil && (
        <div data-testid="over3-paused-banner" className="rounded-lg border border-sky-500/40 bg-sky-500/8 px-3 py-2 text-[11px] font-mono text-sky-300">
          ⏸ Bot paused until {new Date(pausedUntil).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} — you chose to secure your funds. The edge never sleeps, but you can. 💤
        </div>
      )}
      {!srv?.running && srv?.stopped_reason === "daily_cap" && !pausedUntil && (
        <div data-testid="over3-dailycap-banner" className="rounded-lg border border-amber-500/40 bg-amber-500/8 px-3 py-2 text-[11px] font-mono text-amber-300 space-y-1.5">
          <p>🛑 20% daily guard: today&apos;s digit-bot losses reached 20% of your balance, so the bot stopped itself. With the built-in house edge, continuing on a down day usually digs deeper — but it&apos;s your call.</p>
          <div className="flex flex-wrap gap-1.5">
            <button data-testid="over3-cap-stop-day" onClick={() => pauseBot("day")}
              className="rounded-lg bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-500/40 text-[10px] font-bold px-2.5 py-1 hover:brightness-125 transition">🛡 Stop for today (recommended)</button>
            <button data-testid="over3-cap-continue" onClick={() => startServer(true)}
              className="rounded-lg border border-vts-line text-vts-silver/60 text-[10px] font-bold px-2.5 py-1 hover:text-white transition">Continue anyway — I accept the risk</button>
          </div>
        </div>
      )}
      {!srv?.running && srv?.stopped_reason === "auth" && (
        <div data-testid="over3-server-auth-banner" className="rounded-lg border border-vts-loss/40 bg-vts-loss/8 px-3 py-2 text-[11px] font-mono text-vts-loss">
          ⚠️ The 24/7 bot lost its Deriv authorization and couldn't reconnect (your login token expired). Net so far: {(srv.net || 0) >= 0 ? "+" : ""}${(srv.net || 0).toFixed(2)}{(srv.banked || 0) > 0 ? ` · banked +$${Number(srv.banked).toFixed(2)} is safe` : ""}.
          <span className="block text-[10px] text-vts-silver/60 mt-1">Fix: log in with Deriv again, then press START — it picks up your settings (🔁 auto-restart included).</span>
        </div>
      )}
      {!srv?.running && srv?.stopped_reason === "tp" && (
        <div data-testid="over3-server-tp-banner" className="rounded-lg border border-vts-profit/40 bg-vts-profit/8 px-3 py-2 text-[11px] font-mono text-vts-profit">
          🎯 Your last 24/7 session hit its +${srv.tp} take-profit and stopped itself. Net: +${(srv.net || 0).toFixed(2)}
          {!srv.auto_restart && <span className="block text-[10px] text-amber-300 mt-1">🔁 Auto-restart was OFF for this session — tick "Auto-restart after TP" before pressing START (or flip the 🔁 toggle while it runs) to keep it trading after every TP</span>}
        </div>
      )}
      {!srv?.running && srv?.stopped_reason === "sl" && (
        <div data-testid="over3-server-sl-banner" className="rounded-lg border border-vts-loss/40 bg-vts-loss/8 px-3 py-2 text-[11px] font-mono text-vts-loss">
          🛡 Your last 24/7 session hit its −${srv.sl} stop-loss and stopped itself — balance protected. Net: ${(srv.net || 0).toFixed(2)}
        </div>
      )}
      {!srv?.running && srv?.stopped_reason === "ladder_paused" && (
        <div data-testid="over3-server-ladder-banner" className="rounded-lg border border-vts-loss/40 bg-vts-loss/8 px-3 py-2 text-[11px] font-mono text-vts-loss">
          🛑 Bot paused after a full losing streak. Review before restarting. Net: ${(srv.net || 0).toFixed(2)} — it will NOT restart on its own.
        </div>
      )}

      {!running && !srv?.running ? (
        <>
        {advisor && (
          <div data-testid="over3-autostake" className={`flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-lg border px-3 py-2 font-mono ${advisorApplied ? "border-vts-profit/40 bg-vts-profit/8" : "border-sky-400/35 bg-sky-400/8"}`}>
            <span className="text-[10px] font-bold text-sky-300 uppercase tracking-wider">🎯 Auto-Stake</span>
            <span className="text-[10px] text-vts-silver/70">sized for your <b className="text-white">${advisor.bal.toFixed(2)}</b> balance:</span>
            <span className="text-[11px] text-white font-bold">Base ${advisor.base.toFixed(2)}</span>
            <span className="text-[11px] text-white font-bold">SL ${advisor.sl}</span>
            <span className="text-[11px] text-white font-bold">TP ${advisor.tp}</span>
            <span data-testid="over3-autostake-streak" className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${advisor.streak >= M.cover ? "bg-sky-400/15 text-sky-300" : "bg-amber-500/15 text-amber-300"}`}>SL sized for up to a {advisor.streak}-loss streak · the bot itself pauses after {depthCap} lost recovery rounds</span>
            <span data-testid="over3-recommended-balance" className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${advisor.bal >= advisor.needBal ? "bg-vts-profit/10 text-vts-profit/80" : "bg-amber-500/15 text-amber-300"}`}>
              {advisor.bal >= advisor.needBal ? `✓ balance covers the recommended $${advisor.needBal} for full ${M.cover}-loss protection` : `⚠️ recommended balance ≥ $${advisor.needBal} for full ${M.cover}-loss protection at min stake`}
            </span>
            {advisor.streak < M.cover && (
              <span data-testid="over3-autostake-deposit-hint" className="basis-full text-[9px] font-bold text-amber-300">⚠️ Balance too small for the full {M.cover}-loss protection ({M.cover - 1}-loss streaks happen, {M.cover} is rare) — top up to ~${advisor.needBal} to unlock it at the $0.35 base stake</span>
            )}
            {plans && (
              <span data-testid="over3-bestmode-row" className="basis-full flex flex-wrap items-center gap-x-2 gap-y-1 text-[9px] font-mono text-vts-silver/60">
                ⭐ Best mode for ${advisor.bal.toFixed(2)}: <b className="text-emerald-300">{MODES[plans.best].label}</b>
                <span>· odds of TP before SL: Over 2 ~{Math.round(plans.over2.tpOdds * 100)}% vs Under 7 ~{Math.round(plans.under7.tpOdds * 100)}%</span>
                <span>· survives {plans.over2.streak} vs {plans.under7.streak} b2b losses</span>
                {modeChoice !== "auto" && mode !== plans.best && (
                  <button data-testid="over3-bestmode-switch" onClick={() => setModeChoice("auto")}
                    className="rounded-md bg-emerald-500 text-black text-[9px] font-bold px-2 py-0.5 hover:brightness-110 active:scale-95 transition">SWITCH TO {MODES[plans.best].label.toUpperCase()}</button>
                )}
              </span>
            )}
            {advisorApplied ? (
              <span className="ml-auto text-[10px] font-bold text-vts-profit">✓ APPLIED</span>
            ) : (
              <button data-testid="over3-autostake-apply" onClick={applyAdvisor}
                className="ml-auto rounded-md bg-sky-500 text-black text-[10px] font-bold px-3 py-1 hover:brightness-110 active:scale-95 transition">APPLY</button>
            )}
            <span className="basis-full text-[8.5px] text-vts-silver/40 leading-tight">Rule: SL sized to survive <b>{M.cover} back-to-back losses</b> (only ~{M.chance} chance per entry) — the round-{M.cover} recovery still fires instead of being blocked · TP 10% of balance (bank it &amp; stop). Bigger base stake = fewer streaks survivable.</span>
          </div>
        )}
        <div className="flex flex-wrap items-end gap-2">
          <label className="text-[10px] text-vts-silver/55 font-mono">Market
            <select data-testid="over3-symbol" value={pairMode} onChange={(e) => setPairMode(e.target.value)}
              className="mt-0.5 block rounded-lg bg-vts-surface border border-vts-line px-2 py-1.5 text-[12px] text-white font-mono">
              <option value="auto">🔥 Auto — hottest pair</option>
              {SYMBOLS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </label>
          <label className="text-[10px] text-vts-silver/55 font-mono">Recovery depth <span className="text-vts-silver/35">max 6</span>
            <div className="mt-0.5 flex items-center gap-1">
              {[4, 5, 6].map((d) => (
                <button key={d} data-testid={`over3-depth-${d}`} onClick={() => setDepthCap(d)}
                  className={`rounded-lg border px-2.5 py-1.5 text-[11px] font-mono font-black transition-colors ${depthCap === d ? "border-amber-400 bg-amber-400/15 text-amber-300" : "border-vts-line text-vts-silver/50 hover:border-white/30"}`}>
                  {d}
                </button>
              ))}
            </div>
          </label>
          <label className="text-[10px] text-vts-silver/55 font-mono">Base stake ($) <span className="text-vts-silver/35">min $0.35</span>
            <div className="mt-0.5 flex items-center gap-1">
              <input data-testid="over3-stake" value={baseStake} onChange={(e) => setBaseStake(e.target.value)} inputMode="decimal"
                className="block w-20 rounded-lg bg-vts-surface border border-vts-line px-2 py-1.5 text-[12px] text-white font-mono" />
              <button data-testid="over3-stake-min" onClick={() => setBaseStake("0.35")}
                className="rounded-md border border-vts-line px-1.5 py-1 text-[9px] font-bold text-vts-silver/70 hover:border-vts-blue/50 hover:text-white transition">MIN</button>
            </div>
          </label>
          <label className="text-[10px] text-vts-silver/55 font-mono"><Shield className="inline h-3 w-3 mr-0.5" />Stop-loss ($)
            <input data-testid="over3-sl" value={sl} onChange={(e) => setSl(e.target.value)} inputMode="decimal"
              className="mt-0.5 block w-20 rounded-lg bg-vts-surface border border-vts-line px-2 py-1.5 text-[12px] text-white font-mono" />
          </label>
          <label className="text-[10px] text-vts-silver/55 font-mono"><Target className="inline h-3 w-3 mr-0.5" />Take-profit ($)
            <input data-testid="over3-tp" value={tp} onChange={(e) => setTp(e.target.value)} inputMode="decimal"
              className="mt-0.5 block w-20 rounded-lg bg-vts-surface border border-vts-line px-2 py-1.5 text-[12px] text-white font-mono" />
          </label>
          <label className="flex items-center gap-1.5 text-[10px] text-vts-silver/55 font-mono cursor-pointer select-none pb-2">
            <input type="checkbox" data-testid="over3-patience-toggle" checked={patience} onChange={(e) => setPatience(e.target.checked)}
              className="h-3.5 w-3.5 rounded accent-amber-500" />
            🎯 Digit Matrix smart entry <span className="text-vts-silver/35">— VTS proprietary entry filter: waits for favourable digit conditions (recommended)</span>
          </label>
          <label className="flex items-center gap-1.5 text-[10px] text-vts-silver/55 font-mono cursor-pointer select-none pb-2">
            <input type="checkbox" data-testid="over3-autorestart-toggle" checked={autoRestart} onChange={(e) => setAutoRestart(e.target.checked)}
              className="h-3.5 w-3.5 rounded accent-emerald-500" />
            🔁 Auto-restart after TP <span className="text-vts-silver/35">— bank the profit and start a fresh session · SL still stops the bot for good</span>
          </label>
          <label className="flex items-center gap-1.5 text-[10px] text-vts-silver/55 font-mono cursor-pointer select-none pb-2">
            <input type="checkbox" data-testid="over3-server-toggle" checked={serverMode} onChange={(e) => setServerMode(e.target.checked)}
              className="h-3.5 w-3.5 rounded accent-sky-500" />
            ☁️ 24/7 server mode <span className="text-vts-silver/35">— keeps trading with the app closed · SL + TP required · auto hot-pair · 🔒 peak lock banks half of any profit peak automatically</span>
          </label>
          <button data-testid="over3-start" onClick={requestStart}
            className="flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 text-black text-[12px] font-bold px-5 py-2 hover:brightness-110 active:scale-95 transition">
            <Zap className="h-4 w-4" /> START
          </button>
          {/* 🔴 worst case in dollars + risk warning — visible BEFORE starting */}
          {(() => {
            const b = Math.max(0.35, parseFloat(baseStake) || 0.35);
            let deficit = b, total = b;
            for (let i = 0; i < depthCap; i++) {
              const per = Math.max(0.35, r2((deficit * OVERSHOOT()) / SLICES / M.payoutR));
              total = r2(total + per);
              deficit = r2(deficit + per);
            }
            return (
              <div className="w-full space-y-1">
                <p data-testid="over3-worst-case" className="rounded-md border border-red-500/50 bg-red-500/10 px-2.5 py-1.5 text-[10px] font-mono text-red-300">
                  Worst case if the whole {depthCap}-round ladder loses: <b className="text-red-400 text-[12px]">−${total.toFixed(2)}</b> — then the bot pauses itself and will NOT restart on its own.
                </p>
                <p data-testid="over3-risk-warning" className="rounded-md border border-red-500/30 bg-red-500/5 px-2.5 py-1.5 text-[9px] font-mono text-red-300/80">
                  ⚠ {RISK_WARNING}
                </p>
              </div>
            );
          })()}
          {/* 🩺 one-tap server-side connection self-test (prod debugging) */}
          <button data-testid="over3-diag-btn" onClick={runDiag} disabled={diagBusy}
            className="flex items-center gap-1 rounded-lg border border-vts-line bg-vts-surface text-vts-silver/70 hover:text-white text-[10px] font-bold font-mono px-3 py-2 transition disabled:opacity-50">
            {diagBusy ? "🩺 testing…" : "🩺 CONNECTION TEST"}
          </button>
          {diag && (
            <div data-testid="over3-diag-result" className="basis-full space-y-0.5">
              {diag.map((s, i) => (
                <p key={i} className={`text-[9px] font-mono ${s.ok ? "text-vts-profit/80" : "text-vts-loss"}`}>
                  {s.ok ? "✓" : "✗"} {s.step}: {s.detail}
                </p>
              ))}
            </div>
          )}
          <span className="basis-full text-[9px] text-vts-silver/40 font-mono">
            On a loss → the <b>VTS recovery engine</b> takes over on the <b>next-hottest pair</b> → reset to base · pauses after {depthCap} lost recovery rounds · SL is the hard cap
          </span>
        </div>
        </>
      ) : running ? (
        <div className="flex items-center gap-3 flex-wrap text-[11px] font-mono">
          <Loader2 className="h-3.5 w-3.5 animate-spin text-amber-400" />
          <span data-testid="over3-phase" className="text-vts-silver/70">{phaseText}</span>
          <span data-testid="over3-net" className={`text-[14px] font-bold ${net >= 0 ? "text-vts-profit" : "text-vts-loss"}`}>{net >= 0 ? "+" : ""}${net.toFixed(2)}</span>
          <span className="text-vts-silver/60">{wins}W / {losses}L</span>
          <span data-testid="over3-step" className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${recInfo.round === 0 ? "bg-vts-profit/15 text-vts-profit" : "bg-amber-500/15 text-amber-300"}`}>
            {recInfo.round === 0 ? "BASE STAKE" : `RECOVERY R${recInfo.round} · $${curRecStake.toFixed(2)} · down $${recInfo.deficit.toFixed(2)}`}
          </span>
          <span className="text-vts-silver/45">SL −${sl} · TP +${tp}</span>
          {patience && <span data-testid="over3-patience-badge" className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-vts-blue/15 text-vts-blue">🎯 MATRIX</span>}
          {patience && matrixMsg && phase === "gating" && (
            <span data-testid="over3-local-matrix-status" className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-vts-surface text-vts-silver/70 animate-pulse">
              🔎 scanning digits — waiting for the matrix…
            </span>
          )}
          {patience && matrixMsg === "rotating" && phase === "scanning" && (
            <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-500/15 text-amber-300">🔄 losers everywhere — rotating pairs</span>
          )}
          {patience && matrixMsg === "aligned" && phase !== "gating" && (
            <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-vts-profit/15 text-vts-profit">🎯 matrix aligned</span>
          )}
          <button data-testid="over3-autorestart-live-toggle" onClick={() => setAutoRestart((v) => !v)}
            className={`px-1.5 py-0.5 rounded text-[9px] font-bold transition hover:brightness-125 ${autoRestart ? "bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-500/40" : "bg-vts-surface text-vts-silver/50 ring-1 ring-vts-line"}`}>
            🔁 AUTO-RESTART {autoRestart ? "ON" : "OFF"}
          </button>
          {banked > 0 && <span data-testid="over3-banked" className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-vts-profit/15 text-vts-profit">🏦 banked +${banked.toFixed(2)}</span>}
          <button data-testid="over3-stop" onClick={() => stopBot("Stopped by you")}
            className="ml-auto flex items-center gap-1 rounded-lg bg-vts-loss text-white text-[11px] font-bold px-4 py-1.5 hover:brightness-110 active:scale-95 transition">
            <Square className="h-3 w-3" /> STOP
          </button>
        </div>
      ) : null}

      {/* ☁️ 24/7 trade history — same design as the in-app log */}
      {srv?.running && (srv.recent_trades || []).length > 0 && (
        <div data-testid="over3-server-history" className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-bold text-vts-silver/60 uppercase tracking-wider">Trade history</span>
            <span className={`text-[10px] font-mono font-bold ${(srv.net || 0) >= 0 ? "text-vts-profit" : "text-vts-loss"}`}>session {(srv.net || 0) >= 0 ? "+" : ""}${(srv.net || 0).toFixed(2)}</span>
            <span className="text-[9px] text-sky-300 font-mono">☁️ 24/7</span>
          </div>
          <div className="max-h-40 overflow-y-auto divide-y divide-vts-line/30">
            {(srv.recent_trades || []).slice(0, 25).map((t, i) => (
              <div key={t.contract_id || i} className="flex items-center gap-2 py-1 text-[10px] font-mono">
                <span className="w-14 shrink-0 text-vts-silver/45">{t.created_at ? new Date(t.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "—"}</span>
                <span className="w-20 shrink-0 truncate text-vts-silver/60">{symLabel(t.symbol) || t.symbol}</span>
                <span className="text-white">${Number(t.stake).toFixed(2)}</span>
                <span data-testid="over3-server-trade-digit" className={`px-1 rounded font-bold ${t.digit != null ? ((MODES[srv.contract] || M).isWin(t.digit) ? "bg-vts-profit/15 text-vts-profit" : "bg-vts-loss/15 text-vts-loss") : "text-vts-silver/40"}`}>digit {t.digit != null ? t.digit : "—"}</span>
                <span className={`ml-auto font-bold ${t.profit > 0 ? "text-vts-profit" : "text-vts-loss"}`}>{t.profit > 0 ? "+" : ""}{Number(t.profit).toFixed(2)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* RESPONSIBLE-USE WARNING — shown before every start */}
      {showWarn && (
        <div data-testid="over3-warning-modal" className="fixed inset-0 z-[90] grid place-items-center bg-black/80 backdrop-blur-sm p-4">
          <div className="w-full max-w-md rounded-xl border border-amber-500/40 bg-vts-card p-4 space-y-3 shadow-2xl">
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-amber-400" />
              <span className="text-sm font-bold text-white">Use in short sessions only</span>
            </div>
            <div className="text-[11px] text-vts-silver/80 leading-relaxed space-y-2">
              <p>⚠️ Digit contracts carry a <b className="text-amber-300">built-in house edge (~6%)</b> in Deriv&apos;s payout structure. The longer you run this bot, the more that edge works against you — over many trades it is designed to take profits back.</p>
              <p>✅ Best practice: run <b className="text-white">short sessions</b>, set a small take-profit, and <b className="text-white">stop once you hit it</b>. Never leave this bot running all day.</p>
              <p>📈 For long, unattended sessions use the <b className="text-vts-blue">Rise/Fall Auto Bot</b> — its edge comes from accredited strategy performance, not payout odds.</p>
            </div>
            <div className="flex items-center gap-2 pt-1">
              <button data-testid="over3-warning-accept" onClick={() => start()}
                className="flex-1 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 text-black text-[11px] font-bold px-3 py-2 hover:brightness-110 active:scale-95 transition">
                I understand — start short session
              </button>
              <button data-testid="over3-warning-cancel" onClick={() => setShowWarn(false)}
                className="rounded-lg border border-vts-line text-vts-silver/70 text-[11px] font-bold px-3 py-2 hover:text-white transition">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}


      {/* +EV NUDGE — digits have a fixed house edge; the Auto Bot has a measured one */}
      {slStopped && !running && (
        <div data-testid="over3-ev-nudge" className="rounded-lg border border-vts-blue/40 bg-vts-blue/8 p-3 space-y-1.5">
          <p className="text-[11px] font-bold text-white">Tough session. Want a bot with a real, measured edge?</p>
          <p className="text-[10px] text-vts-silver/65 leading-snug">Digit contracts carry a fixed house edge no staking plan can beat. The <span className="text-white font-semibold">24/7 Auto Bot</span> only fires strategies that are live-accredited in simulation (56%+ win rate over 100+ paper trades vs ~52.6% breakeven) — a real statistical edge, with Auto-Secure profit protection.</p>
          <div className="flex flex-wrap gap-2">
            <button data-testid="over3-nudge-pause-5h" onClick={() => pauseBot("5h")}
              className="rounded-lg bg-sky-500/15 text-sky-300 ring-1 ring-sky-500/40 text-[11px] font-bold px-3 py-1.5 hover:brightness-125 transition">⏸ Pause 5h</button>
            <button data-testid="over3-nudge-pause-day" onClick={() => pauseBot("day")}
              className="rounded-lg bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-500/40 text-[11px] font-bold px-3 py-1.5 hover:brightness-125 transition">🛑 Stop for today</button>
            <button data-testid="over3-goto-autobot" onClick={() => { setSlStopped(false); onGoToAutoBot?.(); }}
              className="rounded-lg bg-vts-blue text-white text-[11px] font-bold px-3 py-1.5 hover:bg-vts-blueHover transition">Open the Auto Bot →</button>
            <button onClick={() => setSlStopped(false)} className="rounded-lg border border-vts-line text-vts-silver/60 text-[11px] px-3 py-1.5 hover:text-white transition">Dismiss</button>
          </div>
        </div>
      )}

      {/* Trade history — persists after the bot stops */}
      {log.length > 0 && (
        <div data-testid="over3-history" className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-bold text-vts-silver/60 uppercase tracking-wider">Trade history</span>
            <span className={`text-[10px] font-mono font-bold ${net >= 0 ? "text-vts-profit" : "text-vts-loss"}`}>session {net >= 0 ? "+" : ""}${net.toFixed(2)}</span>
          </div>
          <div className="max-h-40 overflow-y-auto divide-y divide-vts-line/30">
            {log.map((t, i) => (
              <div key={i} className="flex items-center gap-2 py-1 text-[10px] font-mono">
                <span className="w-14 shrink-0 text-vts-silver/45">{new Date(t.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</span>
                <span className="w-20 shrink-0 truncate text-vts-silver/60">{symLabel(t.sym)}</span>
                <span className="text-white">${t.stake.toFixed(2)}</span>
                {t.step > 0 && <span className="rounded bg-amber-500/15 px-1 text-[8.5px] font-bold text-amber-300">R{t.step}</span>}
                <span className="text-vts-silver/55">digit {t.digit != null ? t.digit : "—"}</span>
                <span className={`ml-auto font-bold ${t.won ? "text-vts-profit" : "text-vts-loss"}`}>{t.won ? "+" : ""}{t.profit.toFixed(2)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="text-[8.5px] leading-tight text-vts-silver/35">Digits {M.label} pays ~{M.payoutX}× and wins ~{M.winPct}% of ticks. After any loss the VTS recovery engine takes over with strict caps — the stop-loss is a hard cap on a bad streak; the hot-pair scanner ranks markets by recent hit rate — past digits never guarantee the next tick. Play with money you can lose.</p>

      {/* TAKE-PROFIT CELEBRATION */}
      {tpHit && (
        <div data-testid="over3-tp-modal" className="fixed inset-0 z-[90] grid place-items-center bg-black/70 backdrop-blur-sm p-4">
          <div className="w-full max-w-sm rounded-2xl border border-amber-500/40 bg-vts-card p-6 text-center space-y-3 shadow-2xl">
            <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-gradient-to-br from-amber-400 to-orange-500">
              <PartyPopper className="h-7 w-7 text-black" />
            </div>
            <p className="text-lg font-bold text-white">Take-Profit Hit! 🎯</p>
            <p data-testid="over3-tp-amount" className="text-3xl font-black text-vts-profit">+${tpHit.profit.toFixed(2)}</p>
            {tpHit.restarted && <p className="text-[11px] font-bold text-emerald-300">🔁 Profit banked — a fresh session is already running</p>}
            {tpHit.restartWasOff && <p className="text-[10px] font-bold text-amber-300">🔁 Auto-restart was OFF for this session — tick it before START to keep trading after every TP</p>}
            {tpHit.server && <p className="text-[10px] text-sky-300 font-mono">☁️ 24/7 server session</p>}
            <p className="text-[11px] text-vts-silver/60 font-mono">
              {tpHit.wins}W / {tpHit.losses}L{tpHit.minutes >= 1 ? ` · ${tpHit.minutes.toFixed(0)} min session` : ""} · {M.label} Quick Bot
            </p>
            {tpHit.shared
              ? <p className="text-[10px] text-vts-blue font-mono">📣 Shared to the VTS Telegram channel</p>
              : <p className="text-[10px] text-vts-silver/40 font-mono">Demo sessions aren&apos;t broadcast — go live to get featured</p>}
            <div className="pt-2 border-t border-vts-line space-y-1.5">
              <p className="text-[10px] text-vts-silver/55 font-mono">💡 The house edge never sleeps — profit you walk away with is profit it can&apos;t take back. Secure it?</p>
              <div className="flex flex-wrap items-center justify-center gap-1.5">
                <button data-testid="over3-tp-pause-5h" onClick={() => pauseBot("5h")}
                  className="rounded-lg bg-sky-500/15 text-sky-300 ring-1 ring-sky-500/40 text-[11px] font-bold px-3 py-1.5 hover:brightness-125 transition">⏸ Pause 5h</button>
                <button data-testid="over3-tp-pause-day" onClick={() => pauseBot("day")}
                  className="rounded-lg bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-500/40 text-[11px] font-bold px-3 py-1.5 hover:brightness-125 transition">🛑 Stop for today</button>
                <button data-testid="over3-tp-close" onClick={() => setTpHit(null)}
                  className="rounded-lg bg-vts-blue text-white text-[11px] font-bold px-3 py-1.5 hover:bg-vts-blueHover transition">Continue</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* ANTI-OVERTRADING NUDGE — running again risks giving banked profit back */}
      {overtradePrompt && (
        <div data-testid="over3-overtrade-modal" className="fixed inset-0 z-[93] grid place-items-center bg-black/80 backdrop-blur-sm p-4">
          <div className="w-full max-w-md rounded-xl border border-amber-500/40 bg-vts-card p-4 space-y-3 shadow-2xl">
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-amber-400" />
              <p className="text-sm font-bold text-white">Whoa — running it again?</p>
            </div>
            <p className="text-[12px] text-vts-silver/75 leading-snug">
              {overtradePrompt.bankedToday
                ? <>You already <b className="text-emerald-300">banked a take-profit today</b>. </>
                : <>This would be <b className="text-amber-300">session #{overtradePrompt.sessions + 1} today</b>. </>}
              Every extra session gives the edge another chance to <b className="text-vts-loss">take back your profits</b>.
              The traders who stay green are the ones who walk away.
            </p>
            <div className="flex gap-2">
              <button data-testid="over3-overtrade-stop" onClick={() => { setOvertradePrompt(null); pauseBot("day"); }}
                className="flex-1 rounded-lg bg-emerald-500 text-black text-xs font-bold py-2.5 hover:brightness-110 transition">
                🛑 Smart choice — stop for today
              </button>
              <button data-testid="over3-overtrade-continue" onClick={() => start(true)}
                className="flex-1 rounded-lg border border-vts-line bg-vts-surface text-vts-silver/80 text-xs font-bold py-2.5 hover:text-white transition">
                Continue anyway
              </button>
            </div>
            <button data-testid="over3-overtrade-cancel" onClick={() => setOvertradePrompt(null)}
              className="w-full text-[10px] text-vts-silver/50 hover:text-white">Cancel</button>
          </div>
        </div>
      )}

      {/* 20% DAILY GUARD PROMPT — advisory: the user decides, on the record */}
      {capPrompt && (
        <div data-testid="over3-cap-modal" className="fixed inset-0 z-[92] grid place-items-center bg-black/80 backdrop-blur-sm p-4">
          <div className="w-full max-w-md rounded-xl border border-amber-500/40 bg-vts-card p-4 space-y-3 shadow-2xl">
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-amber-400" />
              <span className="text-sm font-bold text-white">You&apos;re down ${capPrompt.lost} today</span>
            </div>
            <div className="text-[11px] text-vts-silver/80 leading-relaxed space-y-2">
              <p>That&apos;s past your <b className="text-amber-300">20% daily guard</b> (${capPrompt.cap}). Digit contracts carry a built-in house edge — on a losing day, continuing usually digs the hole deeper, not shallower.</p>
              <p>💡 Our honest advice: <b className="text-white">stop for today</b>, come back tomorrow with fresh sizing. But it&apos;s your money and your call.</p>
            </div>
            <div className="flex flex-col gap-1.5 pt-1">
              <button data-testid="over3-cap-modal-stop" onClick={() => pauseBot("day")}
                className="rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 text-black text-[11px] font-bold px-3 py-2 hover:brightness-110 active:scale-95 transition">🛡 Stop for today &amp; secure what&apos;s left (recommended)</button>
              <button data-testid="over3-cap-modal-continue" onClick={() => startServer(true)}
                className="rounded-lg border border-vts-line text-vts-silver/60 text-[11px] font-bold px-3 py-2 hover:text-white transition">Continue anyway — I understand and accept the risk</button>
              <button data-testid="over3-cap-modal-close" onClick={() => setCapPrompt(null)}
                className="text-[10px] text-vts-silver/40 hover:text-white transition">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Over3RecoveryBot;
